import { useState } from "react";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type Status = "idle" | "submitting" | "success" | "error";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahg0ljp14a1h3mr6lf0";

const SubmitForm = () => {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("phone_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("投稿已收到，编辑会尽快核对线索来源。");
      formEl.reset();
      setDetailLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "phone_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("投稿已收到，编辑会尽快核对线索来源，确认公开可查后会排入整理队列。");
        formEl.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setMessage(
          serverMsg && String(serverMsg).trim() !== ""
            ? String(serverMsg)
            : "提交失败，请稍后再试，或通过页面底部的邮箱联系我们。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交没有成功，请稍后再试。");
    }
  };

  return (
    <form
      id="submit"
      data-readdy-form
      onSubmit={handleSubmit}
      className="scroll-mt-28 p-5 md:p-8 rounded-lg bg-background-50 border border-background-200/80"
    >
      <h3 className="font-heading font-black text-xl md:text-2xl text-foreground-950">
        投稿你的线索
      </h3>
      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
        请尽量写清楚事件概述与线索方向，编辑会先做来源核对，再决定是否纳入整理。
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="text-sm font-medium text-foreground-800">事件简述</span>
          <input
            type="text"
            name="event_title"
            required
            placeholder="例如：某博主早年言论与现人设不符"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-800">涉及对象代号</span>
          <input
            type="text"
            name="subject_alias"
            placeholder="可用化名或账号代号"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-800">线索类型</span>
          <select
            name="clue_type"
            defaultValue="旧言论截图"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="旧言论截图">旧言论截图</option>
            <option value="内容变更记录">内容变更记录</option>
            <option value="作品对比线索">作品对比线索</option>
            <option value="数据异常线索">数据异常线索</option>
            <option value="其他方向">其他方向</option>
          </select>
        </label>

        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="flex items-center justify-between text-sm font-medium text-foreground-800">
            <span>线索详情</span>
            <span className="text-xs font-normal text-foreground-500">{detailLength}/500</span>
          </span>
          <textarea
            name="detail"
            rows={5}
            maxLength={500}
            required
            onChange={(event) => setDetailLength(event.target.value.length)}
            placeholder="写清楚时间、平台、可公开核对的线索方向，正文与链接都可以，越具体越好。"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
          />
        </label>

        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="text-sm font-medium text-foreground-800">联系邮箱（便于核实）</span>
          <input
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </label>

        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="case-honeypot"
        />
      </div>

      {status !== "idle" && status !== "submitting" && (
        <p
          role="status"
          className={`mt-5 px-4 py-3 rounded-md text-sm leading-relaxed ${
            status === "success"
              ? "bg-accent-100 text-accent-900"
              : "bg-primary-100 text-primary-900"
          }`}
        >
          {message}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 w-full sm:w-auto px-7 py-3 rounded-md text-sm md:text-base font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {status === "submitting" ? "提交中…" : "提交线索"}
      </button>
      <p className="mt-3 text-xs text-foreground-500 leading-relaxed">
        提交即表示你理解：本站只整理网络公开讨论，不发布任何个人隐私信息，也不接受针对个人的攻击性内容。
      </p>
    </form>
  );
};

export default SubmitForm;